<script setup lang="ts">
import AppDownload from '~/components/user-interface/app-download.vue';
import LazyImage from '~/components/design-system/lazy-image.vue';
import QrCode from '~/components/user-interface/qr-code.vue';
import useDownloadApp from '~/composables/useDownloadApp';

const { getImage } = useBranding();

const { $displayApple, $displayAndroid, $displayHuawei, $displayGooglePlay } =
  useNuxtApp();

interface IAppDownloadProps {
  type?: string;
  direction?: 'vertical' | 'horizontal';
  qrOnly?: boolean;
}

const { downloadLink } = useDownloadApp();

withDefaults(defineProps<IAppDownloadProps>(), {
  type: '',
  direction: 'horizontal',
  qrOnly: false,
});
</script>

<template>
  <NuxtLink :to="downloadLink" target="_blank">
    <div
      v-if="!qrOnly"
      class="flex flex-center gap-2"
      :class="
        direction === 'vertical' ? 'flex-col flex-wrap w-full' : 'flex-row'
      "
    >
      <AppDownload
        v-if="$displayApple"
        :class="{ 'w-full': direction === 'vertical' }"
      >
        <template #icon>
          <LazyImage
            element-name="appleIcon"
            class="dark:invert app-download-icon"
            :src="getImage('apple')"
            alt="Jackpotcity Apple App"
          />
        </template>
        <template #appType>
          {{ $t('app-store') }}
        </template>
      </AppDownload>
      <AppDownload
        v-if="$displayAndroid"
        :class="{ 'w-full': direction === 'vertical' }"
      >
        <template #icon>
          <LazyImage
            element-name="androidIcon"
            :src="getImage('android')"
            alt="Jackpotcity Android App"
            class="app-download-icon"
          />
        </template>
        <template #appType>
          {{ $t('android') }}
        </template>
      </AppDownload>
      <AppDownload
        v-if="$displayHuawei"
        :class="{ 'w-full': direction === 'vertical' }"
      >
        <template #icon>
          <LazyImage
            element-name="huaweiIcon"
            :src="getImage('huawei')"
            alt="Jackpotcity Huawei App"
            class="app-download-icon"
          />
        </template>
        <template #appType>
          {{ $t('app-gallery') }}
        </template>
      </AppDownload>
      <div v-if="$displayGooglePlay" class="w-full">
        <LazyImage
          :class="[
            {
              'h-12': direction === 'vertical',
              'h-10': direction === 'horizontal',
            },
            'cursor-pointer',
          ]"
          element-name="google-play-icon"
          :src="getImage('googlePlay')"
          alt="Jackpotcity Google Play App"
        />
      </div>
    </div>
    <QrCode v-else :url="downloadLink" />
  </NuxtLink>
</template>
<style lang="scss" scoped>
.app-download-icon {
  @apply max-w-4 max-h-4 aspect-square;
}
</style>
